import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, badgeApi, adminApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const POPULAR_BADGE_ICONS = ['🥉', '🥈', '🥇', '💎', '👑', '🎖️', '⭐', '🌟', '🏆', '🔥', '🌱', '💫'];

const COLOR_PRESETS = [
  { name: 'Bronze / Teal', badgeColor: '#0d9488', badgeBgColor: '#f0fdfa', badgeBorderColor: '#5eead4', textColor: '#0f766e' },
  { name: 'Silver / Royal Blue', badgeColor: '#2563eb', badgeBgColor: '#eff6ff', badgeBorderColor: '#93c5fd', textColor: '#1d4ed8' },
  { name: 'Gold / Amber', badgeColor: '#d97706', badgeBgColor: '#fffbeb', badgeBorderColor: '#fcd34d', textColor: '#b45309' },
  { name: 'Platinum / Violet', badgeColor: '#7c3aed', badgeBgColor: '#f5f3ff', badgeBorderColor: '#c4b5fd', textColor: '#6d28d9' },
  { name: 'Diamond / Ruby Red', badgeColor: '#dc2626', badgeBgColor: '#fef2f2', badgeBorderColor: '#fca5a5', textColor: '#991b1b' },
  { name: 'Emerald / Green', badgeColor: '#059669', badgeBgColor: '#ecfdf5', badgeBorderColor: '#6ee7b7', textColor: '#047857' },
];

function AdminBadgesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Modal State for Badge Add/Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    minSessions: 20,
    label: '20+',
    title: 'Bronze Practitioner (20+)',
    description: 'Completed 20+ verified client therapy sessions',
    icon: '🥉',
    badgeColor: '#0d9488',
    badgeBgColor: '#f0fdfa',
    badgeBorderColor: '#5eead4',
    textColor: '#0f766e',
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, badge: null, loading: false });

  // Live Simulator state
  const [simulatedSessions, setSimulatedSessions] = useState(30);

  // Therapists Quick Session Adjuster
  const [therapists, setTherapists] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [sessionEditModal, setSessionEditModal] = useState({ isOpen: false, therapist: null, count: 0, loading: false });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchBadges = useCallback(async () => {
    try {
      setLoading(true);
      const res = await badgeApi.getBadges();
      setBadges(res.badges || []);
    } catch (err) {
      console.error('Failed to load badges:', err);
      showFeedback('error', err.message || 'Failed to load milestone badges');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTherapists = useCallback(async () => {
    try {
      setLoadingTherapists(true);
      const res = await adminApi.getTherapists({ limit: 100 });
      setTherapists(res.therapists || []);
    } catch (err) {
      console.error('Failed to load therapists:', err);
    } finally {
      setLoadingTherapists(false);
    }
  }, []);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchBadges();
    fetchTherapists();
  }, [authSession?.token, navigate, fetchBadges, fetchTherapists]);

  // Open Create Modal
  const handleOpenCreate = () => {
    const nextMin = badges.length > 0 ? Math.max(...badges.map(b => b.minSessions)) + 20 : 20;
    setFormData({
      minSessions: nextMin,
      label: `${nextMin}+`,
      title: `${nextMin}+ Sessions Milestone`,
      description: `Completed ${nextMin}+ verified client therapy sessions`,
      icon: '🎖️',
      badgeColor: '#0d9488',
      badgeBgColor: '#f0fdfa',
      badgeBorderColor: '#5eead4',
      textColor: '#0f766e',
      isActive: true,
    });
    setModalMode('create');
    setEditingId(null);
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (badge) => {
    setFormData({
      minSessions: badge.minSessions,
      label: badge.label,
      title: badge.title,
      description: badge.description || '',
      icon: badge.icon || '🎖️',
      badgeColor: badge.badgeColor || '#0d9488',
      badgeBgColor: badge.badgeBgColor || '#f0fdfa',
      badgeBorderColor: badge.badgeBorderColor || '#5eead4',
      textColor: badge.textColor || '#0f766e',
      isActive: badge.isActive !== false,
    });
    setModalMode('edit');
    setEditingId(badge._id);
    setIsModalOpen(true);
  };

  const handleApplyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      badgeColor: preset.badgeColor,
      badgeBgColor: preset.badgeBgColor,
      badgeBorderColor: preset.badgeBorderColor,
      textColor: preset.textColor,
    }));
  };

  const handleSubmitBadge = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (modalMode === 'create') {
        await badgeApi.adminCreateBadge(formData);
        showFeedback('success', `Milestone badge ${formData.label} created successfully!`);
      } else {
        await badgeApi.adminUpdateBadge(editingId, formData);
        showFeedback('success', `Milestone badge ${formData.label} updated successfully!`);
      }
      setIsModalOpen(false);
      fetchBadges();
    } catch (err) {
      showFeedback('error', err.message || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteBadge = async () => {
    if (!deleteModal.badge) return;
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await badgeApi.adminDeleteBadge(deleteModal.badge._id);
      showFeedback('success', 'Badge removed successfully');
      setDeleteModal({ isOpen: false, badge: null, loading: false });
      fetchBadges();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to delete badge');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all milestone badges back to default 20, 40, 60, 80, 100?')) return;
    try {
      setLoading(true);
      await badgeApi.adminResetBadges();
      showFeedback('success', 'Milestone badges reset to defaults (20, 40, 60, 80, 100)!');
      fetchBadges();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to reset badges');
    } finally {
      setLoading(false);
    }
  };

  // Therapist Session Update Handler
  const handleSaveTherapistSessions = async () => {
    if (!sessionEditModal.therapist) return;
    setSessionEditModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await badgeApi.adminUpdateTherapistSessions(
        sessionEditModal.therapist._id || sessionEditModal.therapist.id,
        sessionEditModal.count
      );
      showFeedback('success', `Updated ${sessionEditModal.therapist.name}'s completed sessions to ${sessionEditModal.count}! Active badge: ${res.badgeInfo?.currentBadge?.label || 'None yet'}`);
      setSessionEditModal({ isOpen: false, therapist: null, count: 0, loading: false });
      fetchTherapists();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to update therapist sessions');
      setSessionEditModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Helper for simulator
  const sortedBadges = [...badges].sort((a, b) => a.minSessions - b.minSessions);
  let simCurrentBadge = null;
  let simNextBadge = null;
  for (let i = 0; i < sortedBadges.length; i++) {
    if (simulatedSessions >= sortedBadges[i].minSessions) {
      simCurrentBadge = sortedBadges[i];
    } else if (!simNextBadge) {
      simNextBadge = sortedBadges[i];
    }
  }

  return (
    <div className="admin-dashboard">
      <DashboardSidebar userRole="admin" />

      <main className="admin-dashboard__main" style={{ marginLeft: '280px', padding: '30px', minHeight: '100vh', background: '#f8fafc' }}>
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '26px', fontWeight: 800, color: '#0f172a' }}>
              🎖️ Practitioner Milestone Badges
            </h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Configure achievement badges (20, 40, 60, 80, 100) with custom colors and thresholds awarded to therapists based on completed sessions.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleResetDefaults}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ↺ Reset Defaults (20-100)
            </button>
            <button
              onClick={handleOpenCreate}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
              }}
            >
              + Add Milestone Badge
            </button>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedback.message && (
          <div style={{
            padding: '14px 20px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontWeight: 600,
            fontSize: '14px',
            background: feedback.type === 'error' ? '#fee2e2' : '#dcfce7',
            color: feedback.type === 'error' ? '#991b1b' : '#166534',
            border: feedback.type === 'error' ? '1px solid #f87171' : '1px solid #86efac',
          }}>
            {feedback.message}
          </div>
        )}

        {/* Live Milestone Simulator Widget */}
        <div style={{
          background: 'linear-gradient(135deg, #1e1335 0%, #2e1065 100%)',
          borderRadius: '16px',
          padding: '22px 26px',
          color: '#ffffff',
          marginBottom: '28px',
          boxShadow: '0 8px 24px rgba(30, 19, 53, 0.2)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px' }}>🔮</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#ffffff' }}>
                  Interactive Badge Logic Simulator
                </h3>
                <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#cbd5e1' }}>
                  Test how completed session counts map to milestone badges (e.g., 30 completed sessions awards the 20+ badge)
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>Simulate Sessions:</label>
              <input
                type="number"
                min="0"
                value={simulatedSessions}
                onChange={(e) => setSimulatedSessions(Math.max(0, parseInt(e.target.value) || 0))}
                style={{
                  width: '90px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1.5px solid #a855f7',
                  background: '#ffffff',
                  color: '#0f172a',
                  fontWeight: 700,
                  fontSize: '15px',
                  textAlign: 'center',
                }}
              />
              <div style={{ display: 'flex', gap: '4px' }}>
                {[15, 30, 50, 75, 110].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSimulatedSessions(s)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: simulatedSessions === s ? '1.5px solid #a855f7' : '1px solid rgba(255,255,255,0.2)',
                      background: simulatedSessions === s ? '#a855f7' : 'rgba(255,255,255,0.1)',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <span style={{ fontSize: '12px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>
                AWARDED MILESTONE BADGE:
              </span>
              {simCurrentBadge ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 16px',
                    borderRadius: '24px',
                    fontSize: '14px',
                    fontWeight: 800,
                    background: simCurrentBadge.badgeBgColor || '#f0fdfa',
                    border: `2px solid ${simCurrentBadge.badgeBorderColor || '#5eead4'}`,
                    color: simCurrentBadge.textColor || '#0f766e',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}>
                    <span>{simCurrentBadge.icon || '🎖️'}</span>
                    <span>{simCurrentBadge.label} ({simCurrentBadge.title})</span>
                  </span>
                  <span style={{ fontSize: '13px', color: '#93c5fd' }}>
                    ✓ Qualified ({simulatedSessions} &gt;= {simCurrentBadge.minSessions} sessions)
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 12px', borderRadius: '16px', fontSize: '13px', color: '#cbd5e1' }}>
                    🌱 Progressing (No badge until 20 sessions)
                  </span>
                  <span style={{ fontSize: '12px', color: '#fcd34d' }}>
                    Need {20 - simulatedSessions} more session(s) to unlock 20+ Bronze
                  </span>
                </div>
              )}
            </div>

            {simNextBadge && (
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '12px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>
                  NEXT MILESTONE TIER:
                </span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#fef08a' }}>
                  {simNextBadge.icon} {simNextBadge.label} ({simNextBadge.minSessions} sessions required)
                </span>
                <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '2px' }}>
                  {Math.max(0, simNextBadge.minSessions - simulatedSessions)} more session(s) to reach {simNextBadge.label}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Milestone Badges Visual Cards Grid */}
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>
          Configured Milestone Tiers ({badges.length})
        </h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading badges...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '36px' }}>
            {badges.map((badge) => (
              <div
                key={badge._id}
                style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  border: `1.5px solid ${badge.badgeBorderColor || '#e2e8f0'}`,
                  padding: '20px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '16px',
                  transition: 'all 0.2s ease',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 800,
                      background: badge.badgeBgColor || '#f0fdfa',
                      border: `1.5px solid ${badge.badgeBorderColor || '#5eead4'}`,
                      color: badge.textColor || '#0f766e',
                    }}>
                      <span>{badge.icon}</span>
                      <span>{badge.label}</span>
                    </span>

                    <span style={{
                      fontSize: '11.5px',
                      fontWeight: 700,
                      background: badge.isActive ? '#dcfce7' : '#fee2e2',
                      color: badge.isActive ? '#15803d' : '#991b1b',
                      padding: '3px 10px',
                      borderRadius: '12px',
                    }}>
                      {badge.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    {badge.title}
                  </h3>
                  <p style={{ margin: '0 0 12px 0', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                    {badge.description || `Awarded after completing ${badge.minSessions}+ client therapy sessions.`}
                  </p>

                  <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Min Sessions:</span>
                      <strong style={{ color: '#0f172a' }}>{badge.minSessions}+</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b' }}>Badge Color:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: badge.badgeColor, display: 'inline-block', border: '1px solid #cbd5e1' }} />
                        <code style={{ fontSize: '11px', color: '#475569' }}>{badge.badgeColor}</code>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b' }}>Border / Text:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: badge.badgeBorderColor, display: 'inline-block', border: '1px solid #cbd5e1' }} />
                        <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: badge.textColor, display: 'inline-block', border: '1px solid #cbd5e1' }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={() => handleOpenEdit(badge)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => setDeleteModal({ isOpen: true, badge, loading: false })}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #fee2e2',
                      background: '#fff',
                      color: '#ef4444',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Therapists Sessions & Milestone Allocation Table */}
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                👨‍⚕️ Therapist Completed Sessions & Active Badges
              </h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                View and adjust completed sessions count for each therapist. The earned milestone badge (20+, 40+, etc.) updates instantly.
              </p>
            </div>
          </div>

          {loadingTherapists ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>Loading therapists...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th style={{ padding: '12px 16px' }}>Therapist Name</th>
                    <th style={{ padding: '12px 16px' }}>Email / Phone</th>
                    <th style={{ padding: '12px 16px' }}>Completed Sessions</th>
                    <th style={{ padding: '12px 16px' }}>Earned Milestone Badge</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {therapists.map((t) => {
                    const sess = t.completedSessions || 0;
                    // Find badge
                    let earned = null;
                    for (let i = 0; i < sortedBadges.length; i++) {
                      if (sess >= sortedBadges[i].minSessions) {
                        earned = sortedBadges[i];
                      }
                    }

                    return (
                      <tr key={t.id || t._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '14px 16px' }}>
                          <strong style={{ color: '#0f172a', fontSize: '14px', display: 'block' }}>{t.name}</strong>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>{t.specialization || 'Mental Health Professional'}</span>
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>
                          <div>{t.email}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>{t.phone}</div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            display: 'inline-block',
                            background: '#f1f5f9',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '14px',
                            color: '#0f172a',
                          }}>
                            {sess} Sessions
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          {earned ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 800,
                              background: earned.badgeBgColor || '#f0fdfa',
                              border: `1.5px solid ${earned.badgeBorderColor || '#5eead4'}`,
                              color: earned.textColor || '#0f766e',
                            }}>
                              <span>{earned.icon}</span>
                              <span>{earned.label} ({earned.title})</span>
                            </span>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                              No badge yet ({sess}/20 sessions)
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <button
                            onClick={() => setSessionEditModal({ isOpen: true, therapist: t, count: sess, loading: false })}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e1',
                              background: '#f8fafc',
                              color: '#7c3aed',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            ✏️ Adjust Sessions
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal: Create / Edit Milestone Badge */}
        {isModalOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '20px',
              maxWidth: '560px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {modalMode === 'create' ? '+ Add New Milestone Badge' : '✏️ Edit Milestone Badge'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', color: '#94a3b8', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {/* Live Preview Box */}
              <div style={{
                background: '#f8fafc',
                border: '1.5px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                textAlign: 'center',
              }}>
                <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                  Live Client & Dashboard Badge Preview
                </span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 18px',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: 800,
                  background: formData.badgeBgColor,
                  border: `2px solid ${formData.badgeBorderColor}`,
                  color: formData.textColor,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}>
                  <span>{formData.icon}</span>
                  <span>{formData.label || `${formData.minSessions}+`}</span>
                </span>
                <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                  {formData.title}
                </div>
              </div>

              <form onSubmit={handleSubmitBadge}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                      Minimum Completed Sessions *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.minSessions}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setFormData({
                          ...formData,
                          minSessions: val,
                          label: formData.label === `${formData.minSessions}+` ? `${val}+` : formData.label,
                        });
                      }}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                      Badge Label * (e.g. 20+, 40+)
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                    Milestone Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Bronze Practitioner (20+)"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                    Icon
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    {POPULAR_BADGE_ICONS.map(ic => (
                      <button
                        key={ic}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: ic })}
                        style={{
                          fontSize: '18px',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          border: formData.icon === ic ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                          background: formData.icon === ic ? '#f5f3ff' : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        {ic}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Presets */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>
                    Quick Color Presets:
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {COLOR_PRESETS.map(cp => (
                      <button
                        key={cp.name}
                        type="button"
                        onClick={() => handleApplyPreset(cp)}
                        style={{
                          fontSize: '11px',
                          padding: '4px 10px',
                          borderRadius: '14px',
                          border: `1px solid ${cp.badgeBorderColor}`,
                          background: cp.badgeBgColor,
                          color: cp.textColor,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {cp.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Inputs */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>
                      Primary Color
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.badgeColor}
                        onChange={(e) => setFormData({ ...formData, badgeColor: e.target.value })}
                        style={{ width: '36px', height: '36px', padding: 0, border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={formData.badgeColor}
                        onChange={(e) => setFormData({ ...formData, badgeColor: e.target.value })}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>
                      Background Color
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.badgeBgColor}
                        onChange={(e) => setFormData({ ...formData, badgeBgColor: e.target.value })}
                        style={{ width: '36px', height: '36px', padding: 0, border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={formData.badgeBgColor}
                        onChange={(e) => setFormData({ ...formData, badgeBgColor: e.target.value })}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>
                      Border Color
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.badgeBorderColor}
                        onChange={(e) => setFormData({ ...formData, badgeBorderColor: e.target.value })}
                        style={{ width: '36px', height: '36px', padding: 0, border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={formData.badgeBorderColor}
                        onChange={(e) => setFormData({ ...formData, badgeBorderColor: e.target.value })}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>
                      Text Color
                    </label>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.textColor}
                        onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                        style={{ width: '36px', height: '36px', padding: 0, border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={formData.textColor}
                        onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    style={{ padding: '10px 22px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {formLoading ? 'Saving...' : (modalMode === 'create' ? 'Create Milestone Badge' : 'Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal: Delete Confirmation */}
        {deleteModal.isOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '420px', width: '100%', padding: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: 800 }}>Delete Milestone Badge?</h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>
                Are you sure you want to remove the <strong>{deleteModal.badge?.label}</strong> ({deleteModal.badge?.title}) badge?
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, badge: null, loading: false })}
                  style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteModal.loading}
                  onClick={handleDeleteBadge}
                  style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Adjust Therapist Sessions */}
        {sessionEditModal.isOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}>
            <div style={{ background: '#fff', borderRadius: '18px', maxWidth: '440px', width: '100%', padding: '26px' }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                ✏️ Adjust Completed Sessions
              </h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
                Practitioner: <strong>{sessionEditModal.therapist?.name}</strong>
              </p>

              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Total Completed Sessions:
                </label>
                <input
                  type="number"
                  min="0"
                  value={sessionEditModal.count}
                  onChange={(e) => setSessionEditModal({ ...sessionEditModal, count: Math.max(0, parseInt(e.target.value) || 0) })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #7c3aed', fontSize: '16px', fontWeight: 700 }}
                />
                <small style={{ color: '#64748b', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                  Tip: Set to 30 to test 20+ badge, 45 to test 40+ badge, 65 for 60+, 85 for 80+, or 110 for 100+!
                </small>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setSessionEditModal({ isOpen: false, therapist: null, count: 0, loading: false })}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={sessionEditModal.loading}
                  onClick={handleSaveTherapistSessions}
                  style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  {sessionEditModal.loading ? 'Updating...' : 'Save & Award Badge'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminBadgesPage;
